export interface AttendanceBreakdown {
	arrived: number; // arrived, within hall capacity
	yetToArrive: number; // within-capacity ticket holders not in yet
	arrivedOver: number; // arrived, beyond hall capacity (over-issue holders who showed)
	overCapacity: number; // over-issue ticket holders not in yet
	emptySeats: number; // hall left unfilled
}

/**
 * Splits a show's attendance against the hall. `issued` is the shared single source
 * (`useEventCapacity().totals.issued`), `scanned` is the door count (`scanHistory.totalScanned`).
 *
 * Issued tickets split into those the hall can seat (`min(issued, capacity)`) and the over-issue beyond it
 * (a legacy over-subscribed show). Within EACH band, arrivals are paired with their not-yet-arrived
 * remainder — and **arrivals fill the within-capacity band first**, only spilling into the over-issue band
 * once everyone the hall can seat has come through the door. So a rendered donut can show, by intensity:
 * `Arrived` / `Yet to arrive` (within hall) and `Arrived over capacity` / `Over capacity` (beyond it).
 *
 * Invariants (donut base = `max(capacity, issued)`):
 *   arrived + arrivedOver           = scanned          (every scan is somewhere)
 *   arrived + yetToArrive           = min(issued, capacity)
 *   arrivedOver + overCapacity      = max(0, issued − capacity)
 *   arrived + yetToArrive + emptySeats = capacity       (when not over-subscribed)
 */
export function splitAttendance(issued: number, capacity: number, scanned: number): AttendanceBreakdown {
	const withinCapacity = Math.min(issued, capacity);
	const overIssued = Math.max(0, issued - capacity);
	const arrived = Math.min(scanned, withinCapacity);
	const arrivedOver = Math.min(Math.max(0, scanned - withinCapacity), overIssued);
	return {
		arrived,
		yetToArrive: withinCapacity - arrived,
		arrivedOver,
		overCapacity: overIssued - arrivedOver,
		emptySeats: Math.max(0, capacity - issued),
	};
}
